import React, { useState, useCallback, useRef } from 'react';
import {
  Alert, Avatar, Box, Button, Card, CardContent, Container, Grid, MenuItem,
  Stack, TextField, Typography, CircularProgress, InputAdornment, IconButton, Link,
  Checkbox, FormControl, FormControlLabel, FormHelperText,
  Dialog, DialogTitle, DialogContent, DialogActions, useTheme
} from '@mui/material';
import { motion } from 'framer-motion';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import SchoolIcon from '@mui/icons-material/School';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { signup } from '../services/authService';
import {
  validateEmail, validatePassword, validateName, validateNumber, sanitizeString
} from '../lib/validation';
import { throttle } from '../lib/rateLimiting';
import { addBreadcrumb } from '../lib/errorReporting';
import { TERMS_CONTENT } from '../content/termsContent';
import DOMPurify from 'dompurify';

const MotionContainer = motion(Container);

// Lightweight markdown -> HTML renderer for our internal T&C content
function mdToHtml(md) {
  if (!md) return '';
  let out = md.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  out = out.replace(/^### (.+)$/gm, '<h4>$1</h4>');
  out = out.replace(/^## (.+)$/gm, '<h3>$1</h3>');
  out = out.replace(/^# (.+)$/gm, '<h2>$1</h2>');
  out = out.replace(/(?:^|- .*(?:\n|$))+?/gm, (match) => {
    if (!/^(- )/m.test(match)) return match;
    const items = match.split(/\n/).filter(Boolean).map(l => l.replace(/^-\s*/, '').trim());
    return '<ul>' + items.map(i => `<li>${i}</li>`).join('') + '</ul>';
  });
  out = out.split(/\n\n+/).map(block => {
    if (/^<h|^<ul/.test(block)) return block;
    return '<p>' + block.replace(/\n/g, '<br/>') + '</p>';
  }).join('');
  return out;
}

const PASSWORD_CONFIG = {
  minLength: 8,
  requireUppercase: true,
  requireLowercase: true,
  requireNumber: true,
  requireSpecial: false,
};

export default function Signup() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    termsAccepted: false,
    preferredLanguage: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [verificationSent, setVerificationSent] = useState(false);
  const [openTerms, setOpenTerms] = useState(false);

  const set = (k) => (e) => {
    const value = e && e.target && e.target.type === 'checkbox' ? e.target.checked : (e && e.target ? e.target.value : e);
    setForm((f) => ({ ...f, [k]: value }));
    if (fieldErrors[k]) {
      setFieldErrors((prev) => ({ ...prev, [k]: null }));
    }
  };

  const throttledSubmitRef = useRef(
    throttle(async (submitFn) => {
      await submitFn();
    }, 2000)
  );

  const validateField = useCallback((field, value) => {
    let result;
    switch (field) {
      case 'firstName':
      case 'lastName':
        result = validateName(
          value,
          field === 'firstName' ? 'First name' : 'Last name',
          { minLength: 2, maxLength: 50 }
        );
        break;
      case 'email':
        result = validateEmail(value);
        break;
      case 'password':
        result = validatePassword(value, PASSWORD_CONFIG);
        break;
      case 'confirmPassword':
        if (!value) {
          result = { isValid: false, error: 'Please re-enter password' };
        } else if (value !== form.password) {
          result = { isValid: false, error: 'Passwords do not match' };
        } else {
          result = { isValid: true };
        }
        break;
      case 'termsAccepted':
        result = value ? { isValid: true } : { isValid: false, error: 'You must accept the Terms and Conditions' };
        break;
      case 'preferredLanguage':
        result = value ? { isValid: true } : { isValid: false, error: 'Please select a language' };
        break;
      default:
        result = { isValid: true };
    }
    return result;
  }, [form]);

  const handleBlur = useCallback((field) => () => {
    const result = validateField(field, form[field]);
    setFieldErrors((prev) => ({ ...prev, [field]: result.isValid ? null : result.error }));
  }, [form, validateField]);

  const openTermsDialog = () => setOpenTerms(true);
  const closeTermsDialog = () => setOpenTerms(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const fields = ['firstName', 'lastName', 'email', 'password', 'confirmPassword', 'preferredLanguage', 'termsAccepted'];
    const newFieldErrors = {};
    let hasErrors = false;

    for (const field of fields) {
      const result = validateField(field, form[field]);
      if (!result.isValid) {
        newFieldErrors[field] = result.error;
        hasErrors = true;
      }
    }

    if (hasErrors) {
      setFieldErrors(newFieldErrors);
      return;
    }

    throttledSubmitRef.current(async () => {
      setLoading(true);
      setVerificationSent(false);

      try {
        addBreadcrumb({ category: 'auth', message: 'Signup attempt', level: 'info' });

        const result = await signup({
          firstName: sanitizeString(form.firstName),
          lastName: sanitizeString(form.lastName),
          email: sanitizeString(form.email),
          password: form.password,
          age: 99,
          classStandard: '12',
          preferredLanguage: form.preferredLanguage,
        });

        if (!result || result.success === false) {
          throw new Error(result?.error || 'Signup failed');
        }
        try { localStorage.setItem('verificationEmail', sanitizeString(form.email)); } catch { }

        setVerificationSent(true);
        addBreadcrumb({ category: 'auth', message: 'Signup successful', level: 'info' });

        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } catch (err) {
        setError(err.message || 'Signup failed. Please try again.');
        addBreadcrumb({ category: 'auth', message: 'Signup failed', level: 'warning', data: { error: err.message } });
      } finally {
        setLoading(false);
      }
    });
  };

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 80px)',
        display: 'flex',
        alignItems: 'center',
        background: 'radial-gradient(circle at 90% 10%, rgba(255, 215, 0, 0.07) 0%, rgba(255, 255, 255, 0) 40%), radial-gradient(circle at 10% 90%, rgba(75, 0, 130, 0.05) 0%, rgba(255, 255, 255, 0) 40%)'
      }}
    >
      <MotionContainer
        maxWidth="sm"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <Card
          elevation={0}
          sx={{
            borderRadius: '16px',
            boxShadow: '0 40px 80px rgba(0,0,0,0.06)',
            border: '1px solid rgba(226, 232, 240, 0.8)',
            mb: 4
          }}
        >
          <CardContent sx={{ p: { xs: 4, sm: 6 } }}>
            <Stack spacing={4}>
              <Stack spacing={1} textAlign="center">
                <Typography variant="h4" fontWeight={800}>Start Your Journey</Typography>
                <Typography color="text.secondary">Join thousands of students cracking exams with Questguru</Typography>
              </Stack>

              {error && (
                <Alert severity="error" variant="standard" sx={{ borderRadius: '16px' }}>
                  {error}
                </Alert>
              )}

              {verificationSent && (
                <Alert severity="success" variant="standard" sx={{ borderRadius: '16px' }}>
                  Verification email sent! Redirecting to login...
                </Alert>
              )}

              <Box component="form" onSubmit={onSubmit} noValidate>
                <Stack spacing={2.5}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5}>
                    <TextField
                      label="First Name"
                      value={form.firstName}
                      onChange={set('firstName')}
                      onBlur={handleBlur('firstName')}
                      error={Boolean(fieldErrors.firstName)}
                      helperText={fieldErrors.firstName}
                      fullWidth
                      disabled={loading}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '16px' } }}
                    />
                    <TextField
                      label="Last Name"
                      value={form.lastName}
                      onChange={set('lastName')}
                      onBlur={handleBlur('lastName')}
                      error={Boolean(fieldErrors.lastName)}
                      helperText={fieldErrors.lastName}
                      fullWidth
                      disabled={loading}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '16px' } }}
                    />
                  </Stack>

                  <TextField
                    label="Email Address"
                    type="email"
                    value={form.email}
                    onChange={set('email')}
                    onBlur={handleBlur('email')}
                    error={Boolean(fieldErrors.email)}
                    helperText={fieldErrors.email}
                    fullWidth
                    disabled={loading}
                    autoComplete="email"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '16px' } }}
                  />

                  <TextField
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={set('password')}
                    onBlur={handleBlur('password')}
                    error={Boolean(fieldErrors.password)}
                    helperText={fieldErrors.password || '8+ chars, with Upper, Lower & Number'}
                    fullWidth
                    disabled={loading}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '16px' } }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" disabled={loading}>
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    label="Confirm Password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={form.confirmPassword}
                    onChange={set('confirmPassword')}
                    onBlur={handleBlur('confirmPassword')}
                    error={Boolean(fieldErrors.confirmPassword)}
                    helperText={fieldErrors.confirmPassword}
                    fullWidth
                    disabled={loading}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '16px' } }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" disabled={loading}>
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    select
                    label="Preferred Language"
                    value={form.preferredLanguage}
                    onChange={set('preferredLanguage')}
                    onBlur={handleBlur('preferredLanguage')}
                    error={Boolean(fieldErrors.preferredLanguage)}
                    helperText={fieldErrors.preferredLanguage}
                    fullWidth
                    disabled={loading}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '16px' } }}
                  >
                    {['English', 'Hindi', 'Bengali', 'Marathi', 'Tamil', 'Telugu', 'Kannada', 'Gujarati', 'Punjabi', 'Urdu', 'Hinglish', 'Other'].map(lang => (
                      <MenuItem key={lang} value={lang}>{lang}</MenuItem>
                    ))}
                  </TextField>

                  <FormControl error={Boolean(fieldErrors.termsAccepted)}>
                    <FormControlLabel
                      control={<Checkbox checked={Boolean(form.termsAccepted)} onChange={set('termsAccepted')} disabled={loading} color="primary" />}
                      label={(
                        <Typography variant="body2" color="text.secondary">
                          I agree to the <Link component="button" type="button" onClick={(e) => { e.preventDefault(); openTermsDialog(); }} sx={{ fontWeight: 700, textDecoration: 'none' }}>Terms & Conditions</Link>
                        </Typography>
                      )}
                    />
                    {fieldErrors.termsAccepted && <FormHelperText>{fieldErrors.termsAccepted}</FormHelperText>}
                  </FormControl>

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading}
                    sx={{
                      py: 1.8,
                      borderRadius: '16px',
                      fontWeight: 700,
                      fontSize: '1rem',
                      boxShadow: '0 10px 20px rgba(255, 215, 0, 0.3)'
                    }}
                    fullWidth
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Create My Free Account'}
                  </Button>

                  <Typography variant="body2" color="text.secondary" align="center">
                    Already have an account? <Link component={RouterLink} to="/login" sx={{ fontWeight: 700, color: 'primary.main', textDecoration: 'none' }}>Log in</Link>
                  </Typography>
                </Stack>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </MotionContainer>

      <Dialog
        open={openTerms}
        onClose={closeTermsDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Terms & Conditions</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ fontSize: 14 }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(mdToHtml(TERMS_CONTENT)) }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeTermsDialog} sx={{ fontWeight: 600 }}>Cancel</Button>
          <Button onClick={() => { setForm((f) => ({ ...f, termsAccepted: true })); closeTermsDialog(); }} variant="contained" sx={{ borderRadius: '16px', fontWeight: 700 }}>I Accept</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
